import React, { useState } from 'react';
import type { DocBrand, BrandConfig, TierConfig } from '../../../types/docops';
import { saveBrand } from '../../../services/firestore/docopsService';
import { C, CONFIG_SECTIONS } from './docopsConstants';

export interface ConfigViewProps {
  activeBrand: DocBrand | null;
  goBack: () => void;
  onBrandSaved: (brand: DocBrand) => void;
}

export const ConfigView: React.FC<ConfigViewProps> = ({
  activeBrand,
  goBack,
  onBrandSaved,
}) => {
  const [configDraft, setConfigDraft] = useState<BrandConfig | null>(
    activeBrand ? { ...activeBrand.config } : null,
  );
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    brand: true, people: false, partners: false,
    financials: false, contacts: false, addresses: false, documents: false, tiers: false,
  });
  const [saving, setSaving] = useState(false);

  const toggleSection = (sectionKey: string) => {
    setOpenSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  const updateConfigField = (key: keyof BrandConfig, value: string | number | string[]) => {
    if (!configDraft) return;
    setConfigDraft({ ...configDraft, [key]: value });
  };

  const updateTierField = (tierKey: 'tier1' | 'tier2' | 'tier3', field: keyof TierConfig, value: string | boolean | null) => {
    if (!configDraft) return;
    setConfigDraft({
      ...configDraft,
      tiers: {
        ...configDraft.tiers,
        [tierKey]: {
          ...configDraft.tiers[tierKey],
          [field]: value,
        },
      },
    });
  };

  const handleSaveConfig = async () => {
    if (!activeBrand || !configDraft) return;
    const updatedBrand: DocBrand = {
      ...activeBrand,
      config: configDraft,
      updatedAt: new Date().toISOString(),
    };
    setSaving(true);
    try {
      await saveBrand(updatedBrand);
      onBrandSaved(updatedBrand);
    } catch (err) {
      console.error('Failed to save brand config:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!activeBrand || !configDraft) {
    return (
      <div>
        <button className="dc-back" onClick={goBack}>{'\u2190'} Back to Pipeline</button>
        <div className="dc-empty">No brand selected.</div>
      </div>
    );
  }

  return (
    <>
      <button className="dc-back" onClick={goBack}>{'\u2190'} Back to Pipeline</button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: C.text }}>Brand Configuration</h2>
        <span className="dc-badge" style={{ color: C.accent, background: C.accentSoft, fontSize: 11, padding: '3px 10px' }}>
          {activeBrand.name}
        </span>
      </div>

      {CONFIG_SECTIONS.map(section => {
        const isOpen = openSections[section.key] ?? false;
        return (
          <div key={section.key} className="dc-section">
            <div className="dc-section-title" onClick={() => toggleSection(section.key)}>
              <span>{section.icon}</span>
              <span>{section.label}</span>
              <span style={{ fontSize: 12, color: C.textMuted, marginLeft: 'auto' }}>
                {isOpen ? '\u25B2' : '\u25BC'}
              </span>
            </div>
            {isOpen && (
              <div style={{ paddingTop: 8 }}>
                {section.fields.map(field => {
                  const rawValue = configDraft[field.key];
                  let displayValue: string;
                  if (field.type === 'array') {
                    displayValue = Array.isArray(rawValue) ? (rawValue as string[]).join(', ') : String(rawValue ?? '');
                  } else if (field.type === 'number') {
                    displayValue = rawValue != null ? String(rawValue) : '';
                  } else {
                    displayValue = (rawValue as string) ?? '';
                  }
                  return (
                    <div key={field.key} className="dc-config-field">
                      <label className="dc-config-label">{field.label}</label>
                      <input
                        className="dc-config-input"
                        type={field.type === 'number' ? 'number' : 'text'}
                        value={displayValue}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (field.type === 'array') {
                            updateConfigField(field.key, val.split(',').map(s => s.trim()));
                          } else if (field.type === 'number') {
                            updateConfigField(field.key, val === '' ? 0 : parseFloat(val));
                          } else {
                            updateConfigField(field.key, val);
                          }
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Tier Configuration */}
      <div className="dc-section">
        <div className="dc-section-title" onClick={() => toggleSection('tiers')}>
          <span>{'\uD83C\uDFAF'}</span>
          <span>Service Tiers</span>
          <span style={{ fontSize: 12, color: C.textMuted, marginLeft: 'auto' }}>
            {openSections.tiers ? '\u25B2' : '\u25BC'}
          </span>
        </div>
        {openSections.tiers && configDraft.tiers && (
          <div style={{ paddingTop: 8 }}>
            {(['tier1', 'tier2', 'tier3'] as const).map(tierKey => {
              const tier = configDraft.tiers[tierKey];
              const label = tierKey === 'tier1' ? 'Tier 1' : tierKey === 'tier2' ? 'Tier 2' : 'Tier 3';
              return (
                <div key={tierKey} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, marginBottom: 12, background: C.bgSoft }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <strong style={{ color: C.text, fontSize: 14 }}>{label}: {tier.name}</strong>
                    <span className="dc-badge" style={{
                      color: tier.pricingModel === 'free' ? C.success : C.amber,
                      background: tier.pricingModel === 'free' ? C.successBg : C.amberBg,
                      fontSize: 10, padding: '2px 8px',
                    }}>
                      {tier.pricingModel.toUpperCase()}
                    </span>
                    <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: C.textSec, cursor: 'pointer' }}>
                      <input type="checkbox" checked={tier.enabled} onChange={(e) => updateTierField(tierKey, 'enabled', e.target.checked)} />
                      Enabled
                    </label>
                  </div>
                  <div className="dc-config-field">
                    <label className="dc-config-label">Name</label>
                    <input className="dc-config-input" value={tier.name} onChange={(e) => updateTierField(tierKey, 'name', e.target.value)} />
                  </div>
                  <div className="dc-config-field">
                    <label className="dc-config-label">Pricing Model</label>
                    <select className="dc-config-input" value={tier.pricingModel} onChange={(e) => updateTierField(tierKey, 'pricingModel', e.target.value)} style={{ cursor: 'pointer' }}>
                      <option value="free">Free</option>
                      <option value="paid">Paid</option>
                      <option value="freemium">Freemium</option>
                    </select>
                  </div>
                  {tier.pricingModel !== 'free' && (
                    <>
                      <div className="dc-config-field">
                        <label className="dc-config-label">Price</label>
                        <input className="dc-config-input" value={tier.price ?? ''} onChange={(e) => updateTierField(tierKey, 'price', e.target.value || null)} />
                      </div>
                      <div className="dc-config-field">
                        <label className="dc-config-label">Price Description</label>
                        <input className="dc-config-input" value={tier.priceDescription ?? ''} onChange={(e) => updateTierField(tierKey, 'priceDescription', e.target.value || null)} />
                      </div>
                      <div className="dc-config-field">
                        <label className="dc-config-label">Refund Window</label>
                        <input className="dc-config-input" value={tier.refundWindow ?? ''} onChange={(e) => updateTierField(tierKey, 'refundWindow', e.target.value || null)} />
                      </div>
                      <div className="dc-config-field">
                        <label className="dc-config-label">Refund Policy</label>
                        <input className="dc-config-input" value={tier.refundPolicy ?? ''} onChange={(e) => updateTierField(tierKey, 'refundPolicy', e.target.value || null)} />
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <button className="dc-btn dc-btn-primary" onClick={handleSaveConfig} disabled={saving}
        style={{ width: '100%', padding: '12px', fontSize: 14 }}>
        {saving ? 'Saving...' : 'Save Configuration'}
      </button>
    </>
  );
};
